import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Shield, Heart, Award } from 'lucide-react';
const fadeInUp = {
  initial: {
    opacity: 0,
    y: 30
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true
  },
  transition: {
    duration: 0.6,
    ease: 'easeOut'
  }
};
export function About() {
  const values = ['Compassion', 'Integrity', 'Excellence', 'Confidentiality', 'Professionalism', 'Respect', 'Empowerment'];
  return <main className="pt-32" data-id="element-86">
      {/* Hero Section */}
      <section className="py-16 bg-medical-gray/30 relative overflow-hidden" data-id="element-87">
        <div className="max-w-7xl mx-auto px-6 text-center relative z-10" data-id="element-88">
          <span className="text-medical-red font-heading font-bold uppercase tracking-[0.3em] mb-6" data-id="element-89">
            Our Legacy & Leadership
          </span>
          <h1 className="text-5xl md:text-7xl font-heading font-extrabold text-medical-blue mb-8 leading-tight" data-id="element-90">
            Restoring Health. <br data-id="element-91" />
            <span className="text-medical-red italic" data-id="element-92">Renewing Minds.</span>
          </h1>
          <p className="text-lg md:text-xl text-medical-blue/70 max-w-3xl mx-auto leading-relaxed font-medium" data-id="element-93">
            We specialize in integrating primary healthcare, lifestyle wellness,
            mental health coaching, critical care consultation, and professional
            training to promote total well-being across the lifespan.
          </p>
        </div>
        <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-[0]" data-id="element-94">
          <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="relative block w-full h-[60px] fill-white" data-id="element-95">
            <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V120H0V95.8C57.23,107.63,128.3,108.13,201.33,101.86,242.71,98.3,282.73,89.14,321.39,56.44Z" data-id="element-96"></path>
          </svg>
        </div>
      </section>

      {/* Leadership Section */}
      <section className="py-20 bg-white" data-id="element-97">
        <div className="max-w-7xl mx-auto px-6" data-id="element-98">
          <div className="grid lg:grid-cols-2 gap-20 items-center mb-32" data-id="element-99">
            <motion.div {...fadeInUp} className="relative" data-id="element-100">
              <div className="organic-shape overflow-hidden bg-medical-blue aspect-square shadow-2xl border-[12px] border-medical-gray/30" data-id="element-101">
                <img src="https://cdn.magicpatterns.com/uploads/tDpvm1x4bUSX1p1PDqUwmQ/Healthcare_Patients.webp" alt="Founder & Clinical Director" className="w-full h-full object-cover" data-id="element-102" />
              </div>
            </motion.div>
            <motion.div {...fadeInUp} data-id="element-103">
              <h2 className="text-4xl font-heading font-extrabold text-medical-blue mb-4 italic" data-id="element-104">
                Founder & Clinical Director
              </h2>
              <p className="text-medical-red font-heading font-bold uppercase tracking-widest mb-8" data-id="element-105">
                Nurse Practitioner • 15+ Years Experience
              </p>
              <p className="text-lg text-medical-blue/70 mb-8 leading-relaxed" data-id="element-106">
                A highly experienced Nurse Practitioner with extensive clinical
                experience in both private and government hospital systems.
              </p>
              <ul className="grid gap-4" data-id="element-107">
                {['Family practice and primary care', 'Critical care support', 'Chronic disease management', 'Preventive health and wellness'].map((item, i) => <li key={i} className="flex items-center gap-4 text-medical-blue font-bold" data-id="element-108">
                    <CheckCircle2 size={20} className="text-medical-red shrink-0" data-id="element-109" />{' '}
                    {item}
                  </li>)}
              </ul>
            </motion.div>
          </div>

          <div className="grid lg:grid-cols-2 gap-20 items-center" data-id="element-110">
            <motion.div {...fadeInUp} className="order-2 lg:order-1" data-id="element-111">
              <h2 className="text-4xl font-heading font-extrabold text-medical-blue mb-4 italic" data-id="element-112">
                Co-Founder & Mental Wellness Director
              </h2>
              <p className="text-medical-red font-heading font-bold uppercase tracking-widest mb-8" data-id="element-113">
                Board-Certified Master Mental Health Coach
              </p>
              <p className="text-lg text-medical-blue/70 mb-8 leading-relaxed" data-id="element-114">
                Over 13 years of leadership and strategic development expertise,
                specializing in marriage restoration and trauma recovery.
              </p>
              <ul className="grid gap-4" data-id="element-115">
                {['Marriage and relationship restoration', 'Grief & Loss support', 'Trauma recovery and emotional healing', 'Life recovery and purpose coaching'].map((item, i) => <li key={i} className="flex items-center gap-4 text-medical-blue font-bold" data-id="element-116">
                    <CheckCircle2 size={20} className="text-medical-red shrink-0" data-id="element-117" />{' '}
                    {item}
                  </li>)}
              </ul>
            </motion.div>
            <motion.div {...fadeInUp} className="relative order-1 lg:order-2" data-id="element-118">
              <div className="organic-shape overflow-hidden bg-medical-blue aspect-square shadow-2xl border-[12px] border-medical-gray/30 rotate-12" data-id="element-119">
                <img src="https://cdn.magicpatterns.com/uploads/e4LVpLJpYC1KKKciqBgn3q/Mental_Health_Coaching_Services.webp" alt="Mental Wellness Director" className="w-full h-full object-cover" data-id="element-120" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-medical-blue text-white relative overflow-hidden" data-id="element-121">
        <div className="absolute top-0 left-0 w-full overflow-hidden leading-[0] rotate-180" data-id="element-122">
          <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="relative block w-full h-[60px] fill-white" data-id="element-123">
            <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V120H0V95.8C57.23,107.63,128.3,108.13,201.33,101.86,242.71,98.3,282.73,89.14,321.39,56.44Z" data-id="element-124"></path>
          </svg>
        </div>
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-20 py-20 relative z-10" data-id="element-125">
          <motion.div {...fadeInUp} className="p-12 bg-white/5 rounded-[48px] border border-white/10" data-id="element-126">
            <Shield className="text-medical-red mb-8" size={48} data-id="element-127" />
            <h2 className="text-4xl font-heading font-extrabold mb-6 italic" data-id="element-128">
              Our Mission
            </h2>
            <p className="text-lg opacity-80 leading-relaxed" data-id="element-129">
              To restore health, promote emotional wellness, and empower
              individuals and families to live healthier, more meaningful, and
              purpose-driven lives.
            </p>
          </motion.div>
          <motion.div {...fadeInUp} className="p-12 bg-white/5 rounded-[48px] border border-white/10" data-id="element-130">
            <Heart className="text-medical-red mb-8" size={48} data-id="element-131" />
            <h2 className="text-4xl font-heading font-extrabold mb-6 italic" data-id="element-132">
              Our Vision
            </h2>
            <p className="text-lg opacity-80 leading-relaxed" data-id="element-133">
              To become a trusted leader in integrated healthcare and mental
              wellness services locally, nationally, and globally.
            </p>
          </motion.div>
        </div>
        <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-[0]" data-id="element-134">
          <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="relative block w-full h-[60px] fill-white" data-id="element-135">
            <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V120H0V95.8C57.23,107.63,128.3,108.13,201.33,101.86,242.71,98.3,282.73,89.14,321.39,56.44Z" data-id="element-136"></path>
          </svg>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-white" data-id="element-137">
        <div className="max-w-7xl mx-auto px-6 text-center" data-id="element-138">
          <motion.h2 {...fadeInUp} className="text-4xl md:text-5xl font-heading font-extrabold text-medical-blue mb-16" data-id="element-139">
            Our Core <span className="text-medical-red" data-id="element-140">Values</span>
          </motion.h2>
          <motion.div {...fadeInUp} className="flex flex-wrap justify-center gap-4" data-id="element-141">
            {values.map(val => <span key={val} className="px-8 py-4 bg-medical-gray/30 rounded-full font-heading font-bold text-medical-blue border border-medical-gray" data-id="element-142">
                {val}
              </span>)}
          </motion.div>
        </div>
      </section>
    </main>;
}